import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Volume2, VolumeX, Globe } from "lucide-react";
import LoadingScreen from "../components/LoadingScreen";
import FloatingPillNav from "../components/FloatingPillNav";
import CustomCursor from "../components/CustomCursor";
import MemoryAIChat from "../components/MemoryAIChat";
import { useLanguage } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SEIKAT — Hybrid Immersive Landing v2
 * Award-grade scroll-driven experience:
 *  - 4 scroll-snap sections (seikat / memory / bouquet / about)
 *  - Parallax on every background (scroll-linked transforms)
 *  - Word-by-word headline reveal (staggered when in view)
 *  - Section 2 (Memory): cinematic pre-chat intro reveals BEFORE the chat box,
 *    then the chat slides up after a delay or scroll progress
 *  - Section 3 (Bouquet): cinematic image carousel reveal on scroll
 *  - Audio toggle, custom cursor, floating pill nav
 */
export default function LandingPage() {
  const navigate = useNavigate();
  const { language, toggleLang } = useLanguage();
  const { cart } = useCart();
  const cartCount = cart?.items?.length || 0;
  const [loading, setLoading] = useState(() => {
    if (typeof window === "undefined") return true;
    return sessionStorage.getItem("seikat_landing_seen") !== "1";
  });
  const [cms, setCms] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [audioOn, setAudioOn] = useState(false);
  const [settings, setSettings] = useState(null);
  const audioRef = useRef(null);
  const sectionRefs = useRef([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cms/landing`).then(r => r.data).catch(() => null),
      axios.get(`${API}/cms/site-settings`).then(r => r.data).catch(() => null)
    ]).then(([landing, st]) => {
      setCms(landing);
      setSettings(st);
    });
  }, []);

  useEffect(() => {
    if (!cms?.sections?.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveIdx(Number(e.target.dataset.idx));
        });
      },
      { threshold: 0.55 }
    );
    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [cms]);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (audioOn) {
      audioRef.current.pause();
      setAudioOn(false);
    } else {
      audioRef.current.play().catch(() => {});
      setAudioOn(true);
    }
  };

  const sections = cms?.sections || [];
  const loadingPhrase = cms?.loading_phrase || (language === "id" ? "Merangkai puisi alam" : "Weaving nature's poetry");

  return (
    <div className="dark-immersive min-h-screen relative" data-testid="landing-page">
      <CustomCursor />
      {loading && (
        <LoadingScreen
          phrase={loadingPhrase}
          duration={2200}
          onComplete={() => {
            try { sessionStorage.setItem("seikat_landing_seen", "1"); } catch (e) {}
            setLoading(false);
          }}
        />
      )}

      {settings?.audio_url && <audio ref={audioRef} src={settings.audio_url} loop preload="none" />}

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: loading ? 0 : 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-6 left-6 lg:top-8 lg:left-10 z-50"
      >
        <span className="font-heading text-2xl tracking-tight text-seikat-bone">
          Seikat<span className="text-seikat-pink">.</span>
        </span>
      </motion.div>

      {/* Top-right action group: lang toggle + login — grouped to prevent overlap */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: loading ? 0 : 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-4 right-5 lg:top-6 lg:right-8 z-50 flex items-center gap-2"
      >
        <button
          onClick={toggleLang}
          aria-label="Toggle language"
          data-testid="landing-lang-toggle"
          data-magnet
          className="flex items-center gap-2 px-3 py-2 rounded-full glass-dark text-seikat-bone hover:text-seikat-pink transition-colors group"
        >
          <Globe size={13} strokeWidth={1.6} className="group-hover:rotate-12 transition-transform duration-500" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase">
            <span className={language === "id" ? "text-seikat-pink" : "text-seikat-bone-muted"}>ID</span>
            <span className="mx-1 text-seikat-bone-muted/40">/</span>
            <span className={language === "en" ? "text-seikat-pink" : "text-seikat-bone-muted"}>EN</span>
          </span>
        </button>
        <button
          onClick={() => navigate("/login")}
          data-testid="landing-login-btn"
          data-magnet
          className="btn-pill text-sm"
        >
          {language === "id" ? "Masuk" : "Sign in"}
        </button>
      </motion.div>

      {settings?.audio_url && (
        <button
          onClick={toggleAudio}
          aria-label="Toggle audio"
          data-testid="landing-audio-toggle"
          data-magnet
          className="fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full glass-dark flex items-center justify-center text-seikat-bone hover:text-seikat-pink transition-colors"
        >
          {audioOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
      )}

      <div className="hidden lg:flex fixed top-1/2 right-8 z-40 -translate-y-1/2 flex-col gap-3" data-testid="section-indicator">
        {sections.map((s, i) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
              activeIdx === i ? "bg-seikat-pink h-8" : "bg-seikat-bone/30 hover:bg-seikat-bone/60"
            }`}
            aria-label={`Go to ${s.id}`}
          />
        ))}
      </div>

      {sections.map((s, idx) => (
        <SectionShowcase
          key={s.id}
          ref={(el) => (sectionRefs.current[idx] = el)}
          idx={idx}
          totalSections={sections.length}
          section={s}
          language={language}
          onNavigate={navigate}
          loadingDone={!loading}
        />
      ))}

      <FloatingPillNav cartCount={cartCount} />
    </div>
  );
}

/* =========================================================== */
/* SECTION SHOWCASE with scroll-linked parallax + text reveal   */
/* =========================================================== */

const SectionShowcase = React.forwardRef(({ idx, totalSections, section, language, onNavigate, loadingDone }, ref) => {
  const localRef = useRef(null);
  const isMemory = section.id === "memory";
  const headline = (language === "id" ? section.headline_id : section.headline_en) || section.headline_id;
  const body = (language === "id" ? section.body_id : section.body_en) || section.body_id;
  const cta = (language === "id" ? section.cta_label_id : section.cta_label_en) || section.cta_label_id;
  const eyebrow = section.eyebrow || "";

  const { scrollYProgress } = useScroll({
    target: localRef,
    offset: ["start end", "end start"]
  });

  // Parallax: background drifts slower, slight zoom; foreground rises slightly
  const bgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const bgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1, 1.05]);
  const contentY = useTransform(scrollYProgress, [0, 0.5, 1], [40, 0, -40]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0.4]);

  // Memory cinematic pre-chat intro — show only after section enters view
  const [chatVisible, setChatVisible] = useState(!isMemory);
  const [sectionInView, setSectionInView] = useState(false);
  useEffect(() => {
    if (!isMemory || !localRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setSectionInView(true); });
      },
      { threshold: 0.35 }
    );
    observer.observe(localRef.current);
    return () => observer.disconnect();
  }, [isMemory]);
  useEffect(() => {
    if (!isMemory || !sectionInView) return;
    const t = setTimeout(() => setChatVisible(true), 1800);
    return () => clearTimeout(t);
  }, [isMemory, sectionInView]);

  const handleCta = () => {
    if (section.cta_href) onNavigate(section.cta_href);
  };

  // Word-by-word stagger reveal
  const renderHeadline = () => {
    const words = headline.replace(/<em>/g, "<<<EM>>>").replace(/<\/em>/g, "<<</EM>>>").split(/\s+/);
    let inEm = false;
    return words.map((raw, wIdx) => {
      let word = raw;
      let openEm = false;
      let closeEm = false;
      if (word.includes("<<<EM>>>")) { openEm = true; word = word.replace("<<<EM>>>", ""); inEm = true; }
      if (word.includes("<<</EM>>>")) { closeEm = true; word = word.replace("<<</EM>>>", ""); }
      const isEm = inEm;
      if (closeEm) inEm = false;
      return (
        <motion.span
          key={wIdx}
          initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: false, amount: 0.4 }}
          transition={{ duration: 0.85, delay: 0.05 * wIdx + (loadingDone ? 0 : 0.4), ease: [0.22, 1, 0.36, 1] }}
          className={`inline-block mr-[0.22em] ${isEm ? "italic text-seikat-pink" : ""}`}
        >
          {word}
        </motion.span>
      );
    });
  };

  return (
    <section
      id={section.id}
      ref={(el) => { localRef.current = el; if (typeof ref === "function") ref(el); else if (ref) ref.current = el; }}
      data-idx={idx}
      data-testid={`landing-section-${section.id}`}
      className="relative min-h-screen w-full overflow-hidden flex items-center"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        {section.media_type === "video" ? (
          <video
            src={section.media_url}
            poster={section.poster_url || undefined}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`section-video-${section.id}`}
          />
        ) : (
          <img
            src={section.media_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`section-image-${section.id}`}
          />
        )}
        <div className="absolute inset-0 bg-seikat-ink" style={{ opacity: section.overlay_opacity ?? 0.55 }} />
        <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink via-seikat-ink/30 to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-16 py-24 grid lg:grid-cols-12 gap-8 items-center"
      >
        <div className={`${isMemory ? "lg:col-span-5" : "lg:col-span-7"} space-y-7`}>
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.4 }}
            transition={{ duration: 0.7, delay: loadingDone ? 0 : 0.3 }}
            className="flex items-center gap-3"
          >
            <span className="w-8 h-px bg-seikat-pink" />
            <span className="eyebrow-immersive">{eyebrow}</span>
          </motion.div>

          <h1
            className="font-heading text-seikat-bone text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight"
            data-testid={`section-headline-${section.id}`}
          >
            {renderHeadline()}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="font-body text-seikat-bone/75 text-base lg:text-lg max-w-xl font-light leading-relaxed"
          >
            {body}
          </motion.p>

          {!isMemory && cta && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="pt-4"
            >
              <button
                onClick={handleCta}
                className="btn-pill btn-pill--solid group"
                data-testid={`section-cta-${section.id}`}
                data-magnet
              >
                {cta}
                <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          )}
        </div>

        {/* Memory section: cinematic pre-chat reveal → chat */}
        {isMemory && (
          <div className="lg:col-span-7 relative min-h-[480px] flex items-center">
            <AnimatePresence mode="wait">
              {!chatVisible ? (
                <motion.div
                  key="prechat"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02, y: -20 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="glass-dark-strong rounded-3xl p-8 lg:p-12 w-full text-center"
                  data-testid="memory-prechat-intro"
                >
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="eyebrow-immersive text-seikat-lime"
                  >
                    {language === "id" ? "AI Memory Curator" : "AI Memory Curator"}
                  </motion.span>
                  <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="font-heading text-2xl lg:text-3xl text-seikat-bone mt-4 italic leading-relaxed"
                  >
                    "{language === "id"
                      ? "Cerita sebentar, kami akan rangkai sisanya."
                      : "Share a moment, we'll weave the rest."}"
                  </motion.p>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9, duration: 0.4 }}
                    className="flex items-center justify-center gap-2 mt-8 text-seikat-bone-muted text-xs tracking-[0.3em] uppercase"
                  >
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  key="chat"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full"
                >
                  <MemoryAIChat
                    language={language}
                    compact
                    onReadyCta={() => onNavigate("/memory-bouquet/create")}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </motion.div>

      <div className="absolute bottom-28 left-6 lg:left-16 z-10 font-body text-seikat-bone/50 text-xs tracking-[0.3em] uppercase">
        <span className="text-seikat-pink">{String(idx + 1).padStart(2, "0")}</span>
        <span className="mx-2">/</span>
        <span>{String(totalSections).padStart(2, "0")}</span>
      </div>

      {/* Scroll progress line per section */}
      <motion.div
        className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-seikat-pink to-seikat-lime origin-left"
        style={{ scaleX: scrollYProgress, width: "100%" }}
      />
    </section>
  );
});
SectionShowcase.displayName = "SectionShowcase";
